import {
  Button,
  LabeledTextarea,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useAlert,
  useToast,
} from "@apex/component-library";
import { Stack } from "@chakra-ui/react";
import { ChangeEvent, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import queryKeys from "../../../api/queryKeys";
import { useCloseMarginCall } from "../../../api/useCloseCall";
import { CallAlertData } from "../../../api/useGetCallOrAlert";
import { GetCallsFromAPI, Status } from "../../../types";
import { SUCCESS_TOAST } from "../../../utils/constants";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  account: string;
  isCall?: boolean;
  onSuccess?: () => void;
}

export const CloseMarginCallOrAlertModal = ({
  account,
  entityId,
  isOpen,
  onClose,
  isCall,
  onSuccess,
}: ModalProps) => {
  const [isTouched, setIsTouched] = useState(false);
  const [note, setNote] = useState("");
  const initialRef = useRef(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { removeAllAlerts } = useAlert();

  const { mutate, isLoading } = useCloseMarginCall(entityId, account, {
    onSuccess: () => {
      toast({
        ...SUCCESS_TOAST,
        description: `The ${isCall ? "margin call" : "alert"} has been closed.`,
      });

      !!queryClient.getQueryState([queryKeys.getCallOrAlert, entityId]) &&
        queryClient.setQueryData<CallAlertData | undefined>(
          [queryKeys.getCallOrAlert, entityId],
          prev => {
            if (prev) {
              return {
                ...prev,
                status: Status.Closed,
              };
            }
            return prev;
          }
        );

      !!queryClient.getQueryState(queryKeys.getMarginCalls) &&
        queryClient.setQueryData(
          [queryKeys.getMarginCalls],
          (prev: GetCallsFromAPI | undefined) => ({
            totalRows: prev!.totalRows,
            records: prev!.records.map(call => {
              if (call.eventId === entityId && call.account === account) {
                return {
                  ...call,
                  status: Status.Closed,
                };
              }
              return call;
            }),
          })
        );
      removeAllAlerts();
      !!onSuccess && onSuccess();
    },
    onSettled: () => {
      handleClose();
    },
  });

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleSubmit = () => {
    mutate(note);
  };

  const handleClose = () => {
    setNote("");
    setIsTouched(false);
    onClose();
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={handleClose}
      initialFocusRef={initialRef}
    >
      <ModalOverlay />
      <ModalContent data-testid="close-call-modal">
        <ModalHeader>Close Margin {isCall ? "Call" : "Alert"}</ModalHeader>
        <ModalCloseButton data-testid="exit-close-call" />
        <ModalBody>
          <LabeledTextarea
            data-testid="close-call-note"
            isInvalid={isTouched && note === ""}
            isRequired={true}
            errorMessage="Required"
            onBlur={() => {
              setIsTouched(true);
            }}
            onChange={handleChange}
            value={note}
            label="Note"
            charCounter={255}
            maxLength={255}
            isDisabled={isLoading}
          />
        </ModalBody>
        <ModalFooter>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="cancel-close-call"
              ref={initialRef}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isDisabled={note === ""}
              data-testid="confirm-close-call"
              isLoading={isLoading}
            >
              Confirm
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
